import {
  IPropertyAnalyticsDailyPoint,
  IPropertyAnalyticsData,
  IPropertyAnalyticsItem,
  IPropertyAnalyticsSummary,
} from "../types/ownerDashboard.types";

function asNumber(value: unknown, fallback = 0): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function normalizeSummary(value: unknown): IPropertyAnalyticsSummary {
  const record = value && typeof value === "object" ? (value as Record<string, unknown>) : {};

  return {
    screen_page_views: asNumber(record.screen_page_views),
    active_users: asNumber(record.active_users),
    screen_page_views_per_active_user: asNumber(record.screen_page_views_per_active_user),
    average_engagement_time_seconds: asNumber(record.average_engagement_time_seconds),
    schedule_click_count: asNumber(record.schedule_click_count),
    schedule_key_event_count: asNumber(record.schedule_key_event_count),
  };
}

function normalizeDailyPoint(value: unknown): IPropertyAnalyticsDailyPoint | null {
  if (!value || typeof value !== "object") {
    return null;
  }

  const record = value as Record<string, unknown>;
  const date = String(record.date ?? "").trim();

  if (!date) {
    return null;
  }

  return {
    date,
    screen_page_views: asNumber(record.screen_page_views),
  };
}

function normalizeItem(value: unknown): IPropertyAnalyticsItem | null {
  if (!value || typeof value !== "object") {
    return null;
  }

  const record = value as Record<string, unknown>;
  const propertyId = Number(record.property_id);

  if (!Number.isFinite(propertyId)) {
    return null;
  }

  const propertyLabel = String(record.property_label ?? "").trim();
  const propertyAddress = String(record.property_address ?? "").trim();
  const pagePath = String(record.page_path ?? "").trim();

  return {
    property_id: propertyId,
    property_label: propertyLabel || null,
    property_address: propertyAddress || null,
    page_path: pagePath || `/imovel/${propertyId}`,
    ...normalizeSummary(record),
  };
}

function sortDailySeries(series: IPropertyAnalyticsDailyPoint[]): IPropertyAnalyticsDailyPoint[] {
  return [...series].sort((first, second) => {
    const [monthA, dayA, yearA] = first.date.split("/");
    const [monthB, dayB, yearB] = second.date.split("/");
    return (
      new Date(`${yearA}-${monthA}-${dayA}`).getTime() -
      new Date(`${yearB}-${monthB}-${dayB}`).getTime()
    );
  });
}

export function normalizePropertyAnalyticsData(raw: unknown): IPropertyAnalyticsData {
  const record = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};

  const dailySeries = Array.isArray(record.daily_series)
    ? record.daily_series
        .map(normalizeDailyPoint)
        .filter((point): point is IPropertyAnalyticsDailyPoint => point != null)
    : [];

  const items = Array.isArray(record.items)
    ? record.items
        .map(normalizeItem)
        .filter((item): item is IPropertyAnalyticsItem => item != null)
        .sort((first, second) => second.screen_page_views - first.screen_page_views)
    : [];

  const error = typeof record.error === "string" ? record.error.trim() : "";

  return {
    configured: Boolean(record.configured),
    error: error || null,
    summary: normalizeSummary(record.summary),
    daily_series: sortDailySeries(dailySeries),
    items,
  };
}

export function isPropertyAnalyticsUnavailable(data: IPropertyAnalyticsData): boolean {
  return data.configured === false || data.error != null;
}
